import React, {
  useCallback, useState, ReactElement, ForwardedRef,
} from 'react';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import InputLabel from '@mui/material/InputLabel';
import OutlinedInput from '@mui/material/OutlinedInput';
import Chip from '../chip/chip.component';
import SearchDropdown from '../search-dropdown/search-dropdown.component';
import RequiredMark from '../required-mark/required-mark.component';
import FieldText from '../field-text/field-text.component';
import TextBlock from '../text-block/text-block.component';
import TextItalic from '../text-italic/text-italic.component';
import FormHelper from '../form-helper/form-helper.component';
import FormatService from '../../services/format-service';
import StructureService from '../../services/structure-service';
import useStylesFieldMultiSelect from './field-multi-select.styles';
import { KEYBOARD_KEY_ENTER, KEYBOARD_KEY_SPACE } from '../../constants/system/keyboard-keys';
import { DATA_REF_NAME } from '../../constants/system/data-attributes';
import NoImage from '../../static/images/no-image.png';

const FieldMultiSelect: ReactMemoComponent = React.memo(React.forwardRef((
  props: ReactComponentProps,
  ref: ForwardedRef<HTMLDivElement>,
): ReactElement => {
  const LABEL_VARIANT_OUTLINED: LabelVariant = 'outlined';
  const PREFIX_NEW_ITEM: string = 'new-item';
  const KEY_FIELD_DEFAULT: string = 'id';
  const ID_PREFIX_MULTI_SELECT_INPUT: string = 'multi-select-input';
  const IMAGE_KEY_DEFAULT: string = 'photo';
  const IMAGE_THUMBNAIL_KEY_DEFAULT: string = 'photoThumbnail';

  const {
    isDisabled,
    isAbleToAdd,
    isReadOnly,
    isBordered,
    isPlain,
    isFullWidth,
    isRequired,
    isOutlined,
    withImage,
    withSearch,
    withUnderline,
    withFormHelper,
    className,
    keyField,
    nameKey,
    imageKey,
    imageThumbnailKey,
    blankElementLabel,
    defaultImage,
    label,
    name,
    searchOptions,
    values,
    changeItemDeps,
    inputRefs,
    inputRefIndex,
    optionList,
    onClose,
    onAddItem,
    onSearch,
    onValuesChange,
    newItemComponent,
    input,
    meta,
    children,
    ...custom
  } = props;

  const {
    touched: isTouched,
    error: formError,
    submitError: formSubmitError,
    modifiedSinceLastSubmit,
  } = (meta || {}) as FormFieldMetaProps;

  const { classes } = useStylesFieldMultiSelect();

  const inputId: string | undefined = (!isPlain && input && input.name
    && `${ID_PREFIX_MULTI_SELECT_INPUT}-${input.name}`) || undefined;
  const inputValues: string | null = !isPlain ? ((input && input.value) || null) : values || null;
  const withErrors: boolean = isTouched && (!!formError || !!formSubmitError) && !modifiedSinceLastSubmit;
  const optionsTotal: number = optionList && optionList.length;

  const borderedClassName: string | undefined = isBordered ? classes.fieldMultiSelectBordered : undefined;
  const outlinedClassName: string | undefined = isOutlined ? classes.fieldMultiSelectOutlined : undefined;
  const noUnderlineClassName: string | undefined = !withUnderline ? classes.fieldMultiSelectNoUnderline : undefined;
  const itemPlainClassName: string | undefined = isPlain ? classes.fieldMultiSelectItemPlain : undefined;
  const fullWidthClassName: string | undefined = isFullWidth ? classes.fieldMultiSelectFullWidth : undefined;

  const [newItemValue, setNewItemValue]: [string, Function] = useState('');

  const clickTextField: MouseEventHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
  ): void => {
    event.stopPropagation();
  }, []);

  const addItem: Function = useCallback((value: string, selectedValues: SelectItem[]): void => {
    const newItemId: number = StructureService.getNewItemId(selectedValues, []);
    const newItem: SelectItem = {
      id: `${PREFIX_NEW_ITEM}-${newItemId + 1}`,
      name: value,
    };

    const isValueValid: boolean = (!selectedValues
      .map((selectedValueItem: SelectItem): string => selectedValueItem.name)
      .includes(value)) && value !== '';
    const newSelectedValues: SelectItem[] = isValueValid ? [...selectedValues, newItem] : [...selectedValues];

    if (onValuesChange) {
      onValuesChange(newSelectedValues || []);
    }

    setNewItemValue('');
  }, [...[changeItemDeps || []]]);

  const onNewItemFieldChange: Function = useCallback((event: React.ChangeEvent): void => {
    setNewItemValue((event.target as HTMLInputElement).value);
  }, []);

  const onNewItemFieldKeyPress: Function = useCallback((
    event: React.KeyboardEvent,
    selectedValues: SelectItem,
  ): void => {
    const isEnterKey: boolean = event.key === KEYBOARD_KEY_ENTER;
    const isSpaceKey: boolean = event.key === KEYBOARD_KEY_SPACE;

    if (isEnterKey || isSpaceKey) {
      event.stopPropagation();

      if (isEnterKey) {
        addItem((event.target as HTMLInputElement).value, [...selectedValues]);

        event.preventDefault();
      }
    }
  }, [...[changeItemDeps || []]]);

  const onNewItemFieldBlur: Function = useCallback((
    event: React.MouseEvent<Element, MouseEvent>, selectedValues: SelectItem[],
  ): void => {
    addItem((event.target as HTMLInputElement).value, [...selectedValues]);

    event.stopPropagation();
  }, []);

  const onDelete: Function = useCallback((value: SelectItem, selectedValues: SelectItem[]): void => {
    const valueIndex: number = selectedValues.indexOf(value);

    if (valueIndex !== FormatService.INDEX_NOT_FOUND) {
      StructureService.deleteListItemByIndex(selectedValues, valueIndex);
    }

    if (onValuesChange) {
      onValuesChange(selectedValues || []);
    }
  }, [...[changeItemDeps || []]]);

  const onChange: Function = useCallback((event: React.ChangeEvent): SelectItem[] => {
    let selectedValues: SelectItem[] = (event.target as HTMLInputElement).value as unknown as SelectItem[];
    const keyFieldValue: string = keyField || KEY_FIELD_DEFAULT;
    const selectedValueIndex: number = selectedValues.length - 1;

    let selectedValue: SelectItem = (event.target as HTMLInputElement).value[selectedValueIndex];
    selectedValues = [...selectedValues].slice(0, selectedValues.length - 1);

    selectedValues.forEach((valueItem: SelectItem, valueIndex: number): void => {
      if (valueItem[keyFieldValue] === selectedValue) {
        StructureService.deleteListItemByIndex(selectedValues, valueIndex);
        selectedValue = null;
      }
    });

    if (selectedValue && optionList && optionList.length) {
      optionList.forEach((optionItem: SelectItem): void => {
        if (optionItem[keyFieldValue] === selectedValue) {
          selectedValues.push(optionItem);

          if (onAddItem) {
            onAddItem(optionItem);
          }
        }
      });
    }

    event.preventDefault();

    if (onValuesChange) {
      onValuesChange(selectedValues || []);
    }

    return selectedValues;
  }, [keyField, optionList, ...[changeItemDeps || []]]);

  const close: Function = useCallback((): void => {
    if (onClose) {
      onClose(searchOptions);
    }
  }, [searchOptions]);

  return (
    <FormControl
      ref={ref}
      error={!isPlain && withErrors}
      className={FormatService.formatClassNames(
        classes.fieldMultiSelect, borderedClassName, outlinedClassName, noUnderlineClassName, className,
      )}
    >
      {
        !!label && (
          <InputLabel htmlFor={inputId} variant={isOutlined ? LABEL_VARIANT_OUTLINED : undefined}>
            {label}
            {
              isRequired && (
                <RequiredMark />
              )
            }
          </InputLabel>
        )
      }
      <Select
        multiple
        displayEmpty
        disabled={isDisabled}
        readOnly={isReadOnly}
        ref={(
          inputRefs ? (ref: HTMLInputElement | null): void => {
            inputRefs.current[inputRefIndex] = ref;
          } : null
        )}
        SelectDisplayProps={{
          [DATA_REF_NAME]: inputRefIndex,
        }}
        onClose={close}
        input={(
          isOutlined && label ? (
            <OutlinedInput
              label={(
                <TextBlock>
                  <TextBlock>
                    {label}
                  </TextBlock>
                  {
                    isRequired && (
                      <RequiredMark />
                    )
                  }
                </TextBlock>
              )}
            />
          ) : undefined
        )}
        label={(
          isOutlined ? (
            <TextBlock>
              {label}
            </TextBlock>
          ) : label
        )}
        renderValue={(selectedValues: SelectItem[]): ReactElement => (
          <div
            className={FormatService.formatClassNames(
              classes.fieldMultiSelectItem, itemPlainClassName, fullWidthClassName,
            )}
          >
            {
              selectedValues && !selectedValues.length && (
                <TextItalic>
                  {(blankElementLabel || blankElementLabel === '') ? blankElementLabel : 'None'}
                </TextItalic>
              )
            }
            {
              selectedValues && !!selectedValues.length
              && selectedValues.map((selectedValueItem: SelectItem): ReactElement => (
                <Chip
                  withDelete
                  key={selectedValueItem.id}
                  isDisabled={isDisabled}
                  label={(nameKey && selectedValueItem[nameKey]) || selectedValueItem.title || selectedValueItem.name}
                  imageLink={(
                    withImage && (selectedValueItem[IMAGE_THUMBNAIL_KEY_DEFAULT] || selectedValueItem[IMAGE_KEY_DEFAULT]
                      || (imageThumbnailKey && selectedValueItem[imageThumbnailKey])
                      || (imageKey && selectedValueItem[imageKey]) || defaultImage || NoImage)
                  )}
                  onDelete={(): void => {
                    onDelete(selectedValueItem, [...selectedValues]);
                  }}
                />
              ))
            }
            {
              isAbleToAdd && (
                <FieldText
                  isPlain
                  value={newItemValue}
                  className={classes.fieldTextNewItem}
                  placeholder="New"
                  inputRefIndex={inputRefIndex}
                  inputProps={{
                    [DATA_REF_NAME]: inputRefIndex,
                  }}
                  onMouseDown={clickTextField}
                  onChange={(event: React.ChangeEvent): void => {
                    onNewItemFieldChange(event);
                  }}
                  onBlur={(event: React.MouseEvent<Element, MouseEvent>): void => {
                    onNewItemFieldBlur(event, [...selectedValues]);
                  }}
                  onKeyDown={(event: React.KeyboardEvent): void => {
                    onNewItemFieldKeyPress(event, [...selectedValues]);
                  }}
                />
              )
            }
          </div>
        )}
        inputProps={{
          id: inputId,
          ...(name ? {
            name,
          } : {}),
        }}
        {...{
          ...input,
          onChange,
          value: inputValues || [],
        }}
        {...custom}
      >
        {
          withSearch && !!onSearch && (
            <SearchDropdown
              searchOptions={searchOptions}
              optionsTotal={optionsTotal}
              onSearch={onSearch}
            />
          )
        }
        {newItemComponent}
        {children}
      </Select>
      {
        !isPlain && withFormHelper && withErrors && (
          <FormHelper isTouched={isTouched} formError={formError || formSubmitError} />
        )
      }
    </FormControl>
  );
}));

export default FieldMultiSelect;
